import styles from './vehicleForm.module.css'
import { DataTypeForm, useDataTypesHooks } from "../../../../../features/data-types"
import { PanelWrapper, Data, FieldType, FormField, useGetCrumbs, O, NumberField, ModalId, useUIActions, FormatType } from "../../../../../features/ui"
import { useActions } from "./actions"
import { useNavigate } from "react-router-dom"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { Vehicle } from "../../../state/types"
import { PlateField } from "../../plate-field/plateField"
import { useMemo, useState } from "react"
import { UnitTypeName, useUnitTypeLoaders } from '../../../../stock'
import { MeasureType, MeasuresTable, MeasureFormModal } from '../../../../common'

export const VehicleForm = () => {
    const { dataType } = useRoutesHooks().parseUrl()
    const vehicle = dataType as O<Vehicle>
    const createMode = !vehicle

    const [weightUnits, setWeightUnits] = useState<O<number>>(vehicle?.weight.units)
    const [maxLoadWeightUnits, setMaxLoadWeightUnits] = useState<O<number>>(vehicle?.maxLoadWeight.units)
    const [loadMeasuresData, setLoadMeasuresData] = useState<Data[]>(vehicle?.loadMeasures || [])
    const [currentLoadMeasure, setCurrentLoadMeasure] = useState<Data>()

    const { formatCustomValue } = useDataTypesHooks()
    const navigate = useNavigate()

    const unitTypeLoaders = useUnitTypeLoaders()

    const { submitVehicle } = useActions()
    const { openModal } = useUIActions()

    const vehicleLabel = vehicle ? `: ${vehicle.name}` : ''
    const title = `${createMode ? 'Crear' : 'Editar'} Vehículo${vehicleLabel}`
    const crumbs = useGetCrumbs(
        'Vehículos',
        RoutePath.VEHICLES,
        vehicle && formatCustomValue(vehicle.plate, FormatType.PLATE),
        vehicle?.id,
        createMode ? 'Crear' : 'Editar'
    )

    const weightUnitType = useMemo(() => unitTypeLoaders().findUnitTypeByName(UnitTypeName.KILOGRAM)!, [])
    const baseMeasureTypes = useMemo(() => [MeasureType.LENGTH, MeasureType.HEIGHT, MeasureType.WIDTH], [])
    const measureTypes = useMemo(() => baseMeasureTypes.filter(measureType =>
        currentLoadMeasure?.type === measureType || !loadMeasuresData.map(loadMeasureData => loadMeasureData.type).includes(measureType)
    ), [loadMeasuresData, currentLoadMeasure])

    const openMeasureModal = () => openModal(ModalId.MEASURE_FORM)

    const onAddMeasure = () => {
        setCurrentLoadMeasure(undefined)
        openMeasureModal()
    }
    
    const onEditMeasure = (measureType: string) => {
        setCurrentLoadMeasure(loadMeasuresData.find(measureData => measureData.type === measureType))
        openMeasureModal()
    }
    
    const onRemoveMeasure = (measureType: string) => {
        const newLoadMeasuresData = loadMeasuresData.filter(loadMeasureData => loadMeasureData.type !== measureType)
        setLoadMeasuresData(newLoadMeasuresData)
    }

    const fields: FormField[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre'
        },
        PlateField({}),
        {
            name: 'weight',
            type: FieldType.NUMBER,
            label: 'Peso',
            hint: 'Peso del camión sin carga.',
            render: () => (
                <div className={styles.weightField}>
                    <NumberField
                        id="weightUnits"
                        defaultValue={weightUnits}
                        className={styles.weightUnits}
                        onChange={setWeightUnits}
                    />
                    {weightUnitType.abbreviation}
                </div>
            )
        },
        {
            name: 'maxLoadWeight',
            type: FieldType.NUMBER,
            label: 'Carga Máxima',
            hint: 'Peso máximo soportado por la caja de carga.',
            render: () => (
                <div className={styles.weightField}>
                    <NumberField
                        id="maxLoadWeightUnits"
                        defaultValue={maxLoadWeightUnits}
                        className={styles.weightUnits}
                        onChange={setMaxLoadWeightUnits}
                    />
                    {weightUnitType.abbreviation}
                </div>
            )
        },
        {
            name: 'loadMeasures',
            type: FieldType.TABLE,
            label: 'Medidas',
            hint: 'Medidas de la caja de carga.',
            render: () => (
                <MeasuresTable
                    title=""
                    rows={loadMeasuresData}
                    disableCreate={baseMeasureTypes.length === loadMeasuresData.length}
                    collapsable
                    onCreate={onAddMeasure}
                    onEditRow={onEditMeasure}
                    onRemoveRow={onRemoveMeasure}
                />
            )
        }
    ]

    const onSubmit = (vehicleData: Data) => {
        submitVehicle(
            vehicleData,
            weightUnits!,
            maxLoadWeightUnits!,
            weightUnitType.id,
            loadMeasuresData,
            createMode
        )
    }

    const onCancel = () => navigate(-1)

    const onSaveMeasure = (newData: Data) => {
        const newLoadMeasuresData = currentLoadMeasure ?
            loadMeasuresData.map(loadMeasureData => loadMeasureData.type === currentLoadMeasure.type ? newData : loadMeasureData) :
            [newData, ...loadMeasuresData]
        setLoadMeasuresData(newLoadMeasuresData)
    }

    return (
        <PanelWrapper title={title} crumbs={crumbs}>
            <DataTypeForm
               formId="vehicle-form"
               fields={fields}
               createMode={createMode}
               onSubmit={onSubmit}
               onCancel={onCancel}
            />
            <MeasureFormModal
                measureData={currentLoadMeasure}
                measureTypes={measureTypes}
                onSubmit={onSaveMeasure}
            />
        </PanelWrapper>
    )
}
